import {
  createSetFilterParams,
  NUMBER_FILTER_PARAMS,
  TEXT_FILTER_PARAMS,
} from '@/pages/Studies/utils/sharedColumnDefs';
import type { ProteoformResultsTableConfig, ProteoformSortField, SampleProteoformPublic } from '@/types';
import type { ColDef } from 'ag-grid-enterprise';

const EPITOPE_COLUMN_MIN_WIDTH = 110;
const TEXT_COLUMN_MIN_WIDTH = 200;

// Just the four fixed columns this table renders. `instrument_run_name` was in the pre-fix
// allowlist and is rejected by `build_proteoform_sort_clause` — not a `ProteoformSortField`.
const FIXED_SORT_FIELDS = [
  'sample_name',
  'sample_group',
  'proteoform_name',
  'proteoform_abundance',
] as const satisfies readonly ProteoformSortField[];

export function tauEpitopeKeys(tableConfig: ProteoformResultsTableConfig | undefined): string[] {
  return orderedEpitopeColumns(tableConfig).map(col => col.key);
}

/** Isoform then PTM, each group sorted by its own `index` — sorting the concatenated array would
 *  interleave the two groups. See `ProteoformResultsTableConfig`'s docstring. */
function orderedEpitopeColumns(tableConfig: ProteoformResultsTableConfig | undefined) {
  if (!tableConfig) return [];
  const isoform = tableConfig.isoform_columns.toSorted((a, b) => a.index - b.index);
  const ptm = tableConfig.ptm_columns.toSorted((a, b) => a.index - b.index);
  return [...isoform, ...ptm];
}

/** Deriving the epitope half from the assay's own panel — the exact set the backend validates
 *  against — is what stops a rendered column being silently unsortable (POR-225/POR-353). */
export function tauSamplesSortFields(tableConfig: ProteoformResultsTableConfig | undefined): string[] {
  return [...FIXED_SORT_FIELDS, ...tauEpitopeKeys(tableConfig)];
}

interface BuildTauSamplesColDefsParams {
  headers: { sampleGroups: string; sampleName: string; proteoformGroup: string; abundanceCpm: string };
  yes: string;
  no: string;
  /** Placeholder for an absent Sample Group, Proteoform Group or Abundance value. */
  empty: string;
  tableConfig: ProteoformResultsTableConfig | undefined;
  sampleNameCellRenderer: ColDef<SampleProteoformPublic>['cellRenderer'];
}

/** Column order mirrors the approved Balsamiq board "Study Detail > Tau Samples Detail". */
export function buildTauSamplesColDefs({
  headers,
  yes,
  no,
  empty,
  tableConfig,
  sampleNameCellRenderer,
}: BuildTauSamplesColDefsParams): ColDef<SampleProteoformPublic>[] {
  // `sample_group` is the one `str | None` here; the other two are required but format defensively.
  // Sample Name is missing a formatter because its cellRenderer means one would never show.
  // Display only: filters and sorting still see the raw value.
  const orEmpty = (formatted: string | undefined) => (formatted === undefined || formatted === '' ? empty : formatted);

  return [
    {
      field: 'sample_group',
      headerName: headers.sampleGroups,
      filter: 'agTextColumnFilter',
      filterParams: TEXT_FILTER_PARAMS,
      minWidth: TEXT_COLUMN_MIN_WIDTH,
      resizable: true,
      sortable: true,
      valueFormatter: ({ value }) => orEmpty(value ?? undefined),
    },
    {
      cellRenderer: sampleNameCellRenderer,
      field: 'sample_name',
      headerName: headers.sampleName,
      filter: 'agTextColumnFilter',
      filterParams: TEXT_FILTER_PARAMS,
      minWidth: TEXT_COLUMN_MIN_WIDTH,
      resizable: true,
      sortable: true,
    },
    {
      field: 'proteoform_name',
      headerName: headers.proteoformGroup,
      filter: 'agTextColumnFilter',
      filterParams: TEXT_FILTER_PARAMS,
      minWidth: TEXT_COLUMN_MIN_WIDTH,
      resizable: true,
      sortable: true,
      valueFormatter: ({ value }) => orEmpty(value ?? undefined),
    },
    {
      field: 'proteoform_abundance',
      headerName: headers.abundanceCpm,
      filter: 'agNumberColumnFilter',
      filterParams: NUMBER_FILTER_PARAMS,
      minWidth: TEXT_COLUMN_MIN_WIDTH,
      resizable: true,
      sortable: true,
      valueFormatter: ({ value }) => orEmpty(value?.toLocaleString()),
    },
    ...buildEpitopeColDefs(tableConfig, yes, no),
  ];
}

/** Shared with the Run Details grid, which reads the same `SampleProteoformPublic` rows. */
export function buildEpitopeColDefs(
  tableConfig: ProteoformResultsTableConfig | undefined,
  yes: string,
  no: string,
): ColDef<SampleProteoformPublic>[] {
  // A declared epitope column is always a real boolean on the wire (`panel.build_epitope_fields`
  // emits every key), so `no` is the correct reading of a false/absent epitope rather than a
  // blank cell — POR-225 forbids empty Yes/No cells outright.
  return orderedEpitopeColumns(tableConfig).map(col => ({
    // `col.key` is the wire key and must stay the colId: it is what the datasource sends as
    // `sort_by` and as the filter map key. `col.label` is display text that can change freely.
    colId: col.key,
    field: col.key,
    headerName: col.label,
    // Wide enough that sizeColumnsToFit can't shrink a dozen-plus columns into unreadable stubs.
    minWidth: EPITOPE_COLUMN_MIN_WIDTH,
    resizable: true,
    sortable: true,
    filter: 'agSetColumnFilter',
    // Static values, not cache-derived: a set filter that scans loaded rows would offer only the
    // values on the current page, so an all-"Yes" page would hide the "No" option entirely.
    filterParams: createSetFilterParams(col.key, ['true', 'false'], value => (value === 'true' ? yes : no)),
    valueFormatter: ({ value }) => (value === true ? yes : no),
  }));
}
